import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from './Sidebar';
import Topnav from './Topnav';
import { port } from '../App';

const ResumeParser = () => {
  const [activeTab, setActiveTab] = useState('directory'); // 'upload' or 'directory'
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState('');
  
  // Upload States
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Edit Modal States
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [editForm, setEditForm] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    PrimaryContact: '',
    HighestQualification: '',
    TotalExperience: '',
    AppliedDesignation: ''
  });

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${port}/root/api/resumes/`, {
        params: {
          page: page,
          page_size: pageSize,
          search: debouncedSearchQuery
        }
      });
      setCandidates(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  // Search Debouncing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1); // Reset page to 1 for new search
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchCandidates();
  }, [page, pageSize, debouncedSearchQuery]);

  const userStr = sessionStorage.getItem('user');
  const userObj = userStr ? JSON.parse(userStr) : null;
  const loginEmpId = userObj?.EmployeeId;
  const userDesignation = userObj?.Disgnation || userObj?.Designation;
  const isAdminOrHr = userDesignation === 'Admin' || userDesignation === 'HR' || userDesignation === 'Recruiter';

  // Fetch recruiters
  useEffect(() => {
    if (isAdminOrHr) {
      axios.get(`${port}/root/ScreeningAssigning/`)
        .then(res => {
          setRecruiters(res.data || []);
        })
        .catch(err => {
          console.error("Error fetching recruiters:", err);
        });
    }
  }, [isAdminOrHr]);

  // Bulk assignment
  const handleBulkAssign = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one candidate');
      return;
    }
    if (!selectedRecruiter) {
      toast.warning('Please select a recruiter to assign to');
      return;
    }
    try {
      const response = await axios.post(`${port}/root/api/resumes/assign/`, {
        candidate_ids: selectedIds,
        assign_to_emp_id: selectedRecruiter,
        login_emp_id: loginEmpId
      });
      toast.success(response.data.message || 'Successfully assigned candidates');
      setSelectedIds([]);
      setSelectedRecruiter('');
      fetchCandidates();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Failed to assign candidates');
    }
  };

  // Handle Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndAddFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    validateAndAddFiles(selectedFiles);
  };

  const validateAndAddFiles = (files) => {
    const validFiles = files.filter(file => {
      const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');
      const isZip = file.type === 'application/zip' || file.name.endsWith('.zip') || file.type === 'application/x-zip-compressed';
      if (!isPdf && !isZip) {
        toast.warning(`${file.name} is not a PDF or ZIP file`);
      }
      return isPdf || isZip;
    });

    setUploadFiles(prev => [...prev, ...validFiles]);
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload Logic
  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) {
      toast.warning('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    const formData = new FormData();

    // Check if zip or multiple files
    const hasZip = uploadFiles.some(f => f.name.endsWith('.zip'));
    if (hasZip) {
      formData.append('file', uploadFiles[0]); // Take first ZIP
      toast.info('Processing ZIP Archive...');
    } else {
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
    }

    try {
      const response = await axios.post(`${port}/root/api/resumes/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      toast.success('Resumes uploaded and parsed successfully');
      setUploadFiles([]);
      setUploadProgress(0);
      setActiveTab('directory');
      fetchCandidates();
    } catch (error) {
      console.error('Error uploading resumes:', error);
      toast.error(error.response?.data?.error || 'Error processing uploads');
    } finally {
      setUploading(false);
    }
  };

  // Delete candidate
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      await axios.delete(`${port}/root/api/resumes/${id}/`);
      toast.success('Candidate deleted successfully');
      setCandidates(prev => prev.filter(c => c.id !== id));
      setSelectedIds(prev => prev.filter(i => i !== id));
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete candidate');
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected candidates?`)) return;

    try {
      await axios.post(`${port}/root/api/resumes/bulk_delete/`, { ids: selectedIds });
      toast.success('Selected candidates deleted successfully');
      setCandidates(prev => prev.filter(c => !selectedIds.includes(c.id)));
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete selected candidates');
    }
  };

  // Export CSV
  const handleExportCsv = async () => {
    try {
      const response = await axios.post(`${port}/root/api/resumes/export_csv/`, 
        { ids: selectedIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidates_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
      toast.error('Failed to export CSV');
    }
  };

  // Export Excel
  const handleExportExcel = async () => {
    try {
      const response = await axios.post(`${port}/root/api/resumes/export_excel/`, 
        { ids: selectedIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidates_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
      toast.error('Failed to export Excel');
    }
  };

  // Edit Candidate Modal
  const openEditModal = (cand) => {
    setEditingCandidate(cand);
    setEditForm({
      FirstName: cand.FirstName || '',
      LastName: cand.LastName || '',
      Email: cand.Email || '',
      PrimaryContact: cand.PrimaryContact || '',
      HighestQualification: cand.HighestQualification || '',
      TotalExperience: cand.TotalExperience || '',
      AppliedDesignation: cand.AppliedDesignation || ''
    });
  };

  const handleEditChange = (e) => {
    setEditForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const name = `${editForm.FirstName} ${editForm.LastName}`.trim();
      const updatedData = {
        name: name,
        Email: editForm.Email,
        PrimaryContact: editForm.PrimaryContact,
        HighestQualification: editForm.HighestQualification,
        TotalExperience: editForm.TotalExperience,
        AppliedDesignation: editForm.AppliedDesignation
      };

      const response = await axios.patch(`${port}/root/api/resumes/${editingCandidate.id}/`, updatedData);
      toast.success('Candidate profile updated');
      setCandidates(prev => prev.map(c => c.id === editingCandidate.id ? response.data : c));
      setEditingCandidate(null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update profile');
    }
  };

  // Checkbox handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCandidates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCandidates.map(c => c.id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(i => i !== id));
    } else {
      setSelectedIds(prev => [...prev, id]);
    }
  };

  const filteredCandidates = candidates;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 poppins">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Topnav />
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header section */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Resume Parser Pipeline</h1>
                <p className="text-gray-500 text-sm mt-1">Upload resumes to parse candidate information and manage profiles</p>
              </div>

              {/* Tab Selector */}
              <div className="bg-white border rounded-lg p-1 flex">
                <button
                  onClick={() => setActiveTab('directory')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'directory' 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Parsed Candidates
                </button>
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'upload' 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Upload & Parse Resumes
                </button>
              </div>
            </div>

            {/* TAB: UPLOAD RESUMES */}
            {activeTab === 'upload' && (
              <div className="bg-white rounded-xl border shadow-sm p-8">
                <div 
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors bg-gray-50"
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    type="file"
                    id="file-input"
                    multiple
                    className="hidden"
                    accept=".pdf,.zip"
                    onChange={handleFileSelect}
                  />
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mb-4">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-700 text-lg">Drag & Drop Resumes here</h3>
                  <p className="text-gray-400 text-sm mt-1">Supports single/multiple PDF files or a bulk ZIP folder</p>
                  <button className="mt-4 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-50">
                    Browse Files
                  </button>
                </div>

                {uploadFiles.length > 0 && (
                  <div className="mt-8 border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                      <span className="font-medium text-gray-700 text-sm">{uploadFiles.length} file(s) selected</span>
                      <button 
                        onClick={() => setUploadFiles([])} 
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    <ul className="divide-y max-h-60 overflow-y-auto">
                      {uploadFiles.map((file, idx) => (
                        <li key={idx} className="px-4 py-3 flex justify-between items-center text-sm">
                          <div className="flex items-center space-x-3">
                            <span className="text-gray-400">
                              {file.name.endsWith('.zip') ? '📦' : '📄'}
                            </span>
                            <span className="text-gray-700 font-medium truncate max-w-xs">{file.name}</span>
                            <span className="text-gray-400 text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); removeUploadFile(idx); }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Progress bar */}
                {uploading && (
                  <div className="mt-6">
                    <div className="flex justify-between text-sm mb-1 text-gray-600">
                      <span>Uploading & Parsing...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleUploadSubmit}
                    disabled={uploadFiles.length === 0 || uploading}
                    className="px-6 py-2.5 rounded-lg text-white font-medium text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm transition-colors"
                  >
                    Start Extraction Pipeline
                  </button>
                </div>
              </div>
            )}

            {/* TAB: CANDIDATES DIRECTORY */}
            {activeTab === 'directory' && (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                {/* Actions & Search panel */}
                <div className="p-4 border-b bg-gray-50 flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="relative w-full md:w-80">
                    <input
                      type="text"
                      placeholder="Search name, email, phone, role..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500"
                    />
                    <svg className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end items-center">
                    {selectedIds.length > 0 && (
                      <>
                        {isAdminOrHr && (
                          <div className="flex items-center border rounded-lg bg-white overflow-hidden text-sm h-[38px] shadow-sm">
                            <select
                              value={selectedRecruiter}
                              onChange={(e) => setSelectedRecruiter(e.target.value)}
                              className="px-3 h-full focus:outline-none bg-transparent cursor-pointer text-gray-700 border-r"
                            >
                              <option value="">Select Recruiter...</option>
                              {recruiters.map((rec) => (
                                <option key={rec.EmployeeId} value={rec.EmployeeId}>
                                  {rec.Name || rec.EmployeeId} ({rec.EmployeeId})
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={handleBulkAssign}
                              className="px-4 h-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors"
                            >
                              Assign
                            </button>
                          </div>
                        )}
                        <button
                          onClick={handleBulkDelete}
                          className="px-3.5 py-2 border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors h-[38px]"
                        >
                          Delete Selected ({selectedIds.length})
                        </button>
                        <button
                          onClick={handleExportCsv}
                          className="px-3.5 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 transition-colors h-[38px]"
                        >
                          Export CSV
                        </button>
                        <button
                          onClick={handleExportExcel}
                          className="px-3.5 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 transition-colors h-[38px]"
                        >
                          Export Excel
                        </button>
                      </>
                    )}
                    <button
                      onClick={fetchCandidates}
                      className="p-2 border rounded-lg bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 h-[38px]"
                      title="Refresh"
                    >
                      🔄
                    </button>
                  </div>
                </div>

                {/* Grid Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 bg-white">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="py-3 px-4 text-center border-b border-slate-200 w-10">
                          <input
                            type="checkbox"
                            checked={filteredCandidates.length > 0 && selectedIds.length === filteredCandidates.length}
                            onChange={toggleSelectAll}
                            className="rounded text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          />
                        </th>
                        <th className="py-3 px-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider w-12">S.No</th>
                        <th className="py-3 px-4 text-left border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Candidate Name</th>
                        <th className="py-3 px-4 text-left border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Email ID</th>
                        <th className="py-3 px-4 text-left border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Contact Number</th>
                        <th className="py-3 px-4 text-left border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Position Applied</th>
                        <th className="py-3 px-4 text-left border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Qualifications</th>
                        <th className="py-3 px-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Experience</th>
                        <th className="py-3 px-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Date Applied</th>
                        <th className="py-3 px-4 text-center border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {loading ? (
                        <tr>
                          <td colSpan="10" className="p-12 text-center text-gray-500">
                            <div className="flex justify-center items-center space-x-2">
                              <span className="w-4 h-4 rounded-full bg-blue-600 animate-pulse"></span>
                              <span className="w-4 h-4 rounded-full bg-blue-600 animate-pulse delay-100"></span>
                              <span className="w-4 h-4 rounded-full bg-blue-600 animate-pulse delay-200"></span>
                            </div>
                            <span className="text-sm mt-2 block font-medium">Fetching Candidate Profiles...</span>
                          </td>
                        </tr>
                      ) : filteredCandidates.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="p-12 text-center text-gray-400 text-sm">
                            No candidate profiles found. Click "Upload & Parse Resumes" to add candidates.
                          </td>
                        </tr>
                      ) : (
                        filteredCandidates.map((cand, idx) => {
                          const fullName = `${cand.FirstName || ''} ${cand.LastName || ''}`.trim() || 'N/A';
                          return (
                            <tr key={cand.id} className="hover:bg-slate-50 transition-colors">
                              <td className="py-3 px-4 text-center border-b border-slate-100">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(cand.id)}
                                  onChange={() => toggleSelectOne(cand.id)}
                                  className="rounded text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                              </td>
                              <td className="py-3 px-4 text-center border-b border-slate-100 text-sm text-slate-500">
                                {idx + 1 + (page - 1) * pageSize}
                              </td>
                              <td className="py-3 px-4 text-left font-semibold text-slate-800 border-b border-slate-100 text-sm whitespace-nowrap">
                                {fullName}
                              </td>
                              <td className="py-3 px-4 text-left border-b border-slate-100 text-sm text-slate-600 whitespace-nowrap">
                                {cand.Email || '—'}
                              </td>
                              <td className="py-3 px-4 text-left border-b border-slate-100 text-sm text-slate-600 whitespace-nowrap">
                                {cand.PrimaryContact ? (
                                  <a
                                    href={`tel:${cand.PrimaryContact}`}
                                    className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold"
                                  >
                                    {cand.PrimaryContact}
                                  </a>
                                ) : '—'}
                              </td>
                              <td className="py-3 px-4 text-left font-semibold text-slate-700 border-b border-slate-100 text-sm whitespace-nowrap">
                                {cand.AppliedDesignation || '—'}
                              </td>
                              <td className="py-3 px-4 text-left border-b border-slate-100 text-sm text-slate-600">
                                {cand.HighestQualification || '—'}
                              </td>
                              <td className="py-3 px-4 text-center border-b border-slate-100 text-sm text-slate-600 whitespace-nowrap">
                                {cand.TotalExperience || '—'}
                              </td>
                              <td className="py-3 px-4 text-center border-b border-slate-100 text-sm text-slate-500 whitespace-nowrap">
                                {cand.AppliedDate ? new Date(cand.AppliedDate).toLocaleDateString('en-GB').replace(/\//g, '-') : '—'}
                              </td>
                              <td className="py-3 px-4 text-center border-b border-slate-100 text-sm font-medium space-x-3 whitespace-nowrap">
                                {cand.resume_url && (
                                  <a
                                    href={`${port.replace(/\/$/, '')}${cand.resume_url}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                                    title="View PDF"
                                  >
                                    📄 PDF
                                  </a>
                                )}
                                <button
                                  onClick={() => openEditModal(cand)}
                                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(cand.id)}
                                  className="text-red-600 hover:text-red-800 hover:underline font-semibold"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Footer */}
                <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      Showing <span className="font-semibold">{Math.min((page - 1) * pageSize + 1, totalCount)}</span> to{' '}
                      <span className="font-semibold">{Math.min(page * pageSize, totalCount)}</span> of{' '}
                      <span className="font-semibold">{totalCount}</span> candidates
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="ml-3 px-2 py-1 text-xs border rounded bg-white text-gray-700 focus:outline-none cursor-pointer"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    
                    <span className="px-3.5 py-1.5 text-sm font-medium bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg">
                      Page {page} of {Math.ceil(totalCount / pageSize) || 1}
                    </span>

                    <button
                      onClick={() => setPage(prev => Math.min(prev + 1, Math.ceil(totalCount / pageSize)))}
                      disabled={page >= Math.ceil(totalCount / pageSize)}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EDIT MODAL DIALOG */}
      {editingCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800 text-lg">Edit Candidate Profile</h3>
              <button onClick={() => setEditingCandidate(null)} className="text-gray-400 hover:text-gray-600 font-semibold text-lg">✕</button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">First Name</label>
                  <input
                    type="text"
                    name="FirstName"
                    value={editForm.FirstName}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Last Name</label>
                  <input
                    type="text"
                    name="LastName"
                    value={editForm.LastName}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email</label>
                <input
                  type="email"
                  name="Email"
                  value={editForm.Email}
                  onChange={handleEditChange}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Phone</label>
                <input
                  type="text"
                  name="PrimaryContact"
                  value={editForm.PrimaryContact}
                  onChange={handleEditChange}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Applied Role</label>
                <input
                  type="text"
                  name="AppliedDesignation"
                  value={editForm.AppliedDesignation}
                  onChange={handleEditChange}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Qualifications</label>
                  <input
                    type="text"
                    name="HighestQualification"
                    value={editForm.HighestQualification}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Experience</label>
                  <input
                    type="text"
                    name="TotalExperience"
                    value={editForm.TotalExperience}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditingCandidate(null)}
                  className="px-4 py-2 border rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeParser;
